// Copyright 2022, Imprivata, Inc.  All rights reserved.

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  useCreateEnrollmentMutation,
  useGetGrantStatusQuery,
  useGetInquiryQuery,
  useGetPatientQuery,
  useGetPhotoQuery,
  usePostGrantMutation,
} from '../../../api/hooks';
import { InquiryStatus } from '../../../api/hooks/getInquiryQuery';
import {
  type EnrollPageError,
  useEnrollPageErrorHandling,
} from '../hooks/useEnrollPageErrorHandling';

export const useEnrollPageState = () => {
  const [error, setError] = useState<EnrollPageError>();
  const [showPhoto, setShowPhoto] = useState(false);
  const [enrollmentSuccess, setEnrollmentSuccess] = useState(false);
  const [continueOnOriginalDevice, setContinueOnOriginalDevice] = useState(false);
  const [showPatientAlreadyEnrolledError, setShowPatientAlreadyEnrolledError] = useState(false);
  const [showMultipleRecordsFoundError, setShowMultipleRecordsFoundError] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check if this is Apple Digital ID flow (patientId in query param)
  const appleDigitalIdPatientId = searchParams.get('patientId');

  const {
    patient,
    error: patientError,
    isLoading: isPatientLoading,
    enable: enablePatientQuery,
  } = useGetPatientQuery(false);

  const { photo, isLoading: isPhotoLoading } = useGetPhotoQuery(patient?.photoUrl);

  const {
    grantStatus,
    isLoading: isGrantStatusLoading,
    enable: enableGrantStatusQuery,
  } = useGetGrantStatusQuery(false);

  const {
    createEnrollment,
    apiError: createEnrollmentError,
    isPending: isEnrollmentPending,
  } = useCreateEnrollmentMutation({
    onSuccess: (_) => {
      enablePatientQuery();
      enableGrantStatusQuery();
      setShowPhoto(true);
    },
  });

  // Handle inquiry-based flow (skip if Apple Digital ID)
  const { isLoading: isInquiryLoading, error: inquiryError } = useGetInquiryQuery({
    onSuccess: (inquiryId, status, isAssociatedWithSession) => {
      if (isAssociatedWithSession && status === InquiryStatus.SUCCESS) {
        createEnrollment(inquiryId);
      } else {
        setContinueOnOriginalDevice(true);
      }
    },
  });

  // Handle Apple Digital ID flow - enrollment already happened in AccountPage,
  // just enable queries to load patient data
  useEffect(() => {
    if (appleDigitalIdPatientId) {
      enablePatientQuery();
      enableGrantStatusQuery();
      setShowPhoto(true);
    }
  }, [appleDigitalIdPatientId, enablePatientQuery, enableGrantStatusQuery]);

  const { postGrant } = usePostGrantMutation({});

  // Use error handling hook
  useEnrollPageErrorHandling({
    createEnrollmentError,
    patientError,
    patient,
    isPatientLoading,
    inquiryError,
    error,
    grantStatus,
    isGrantStatusLoading,
    onSetEnrollmentSuccess: setEnrollmentSuccess,
    onSetError: setError,
    onEnablePatientQuery: enablePatientQuery,
    onEnableGrantStatusQuery: enableGrantStatusQuery,
    onSetShowPatientAlreadyEnrolledError: setShowPatientAlreadyEnrolledError,
    onSetShowMultipleRecordsFoundError: setShowMultipleRecordsFoundError,
    onSetShowPhoto: setShowPhoto,
  });

  return {
    state: {
      continueOnOriginalDevice,
      createEnrollmentError,
      enrollmentSuccess,
      error,
      grantStatus,
      inquiryError,
      isEnrollmentPending,
      isGrantStatusLoading,
      isInquiryLoading,
      isPatientLoading,
      isPhotoLoading,
      patient,
      patientError,
      photo,
      showPatientAlreadyEnrolledError,
      showMultipleRecordsFoundError,
      showPhoto,
    },
    actions: {
      createEnrollment,
      enableGrantStatusQuery,
      enablePatientQuery,
      navigate,
      postGrant,
      setContinueOnOriginalDevice,
      setEnrollmentSuccess,
      setError,
      setShowPatientAlreadyEnrolledError,
      setShowMultipleRecordsFoundError,
      setShowPhoto,
    },
  };
};
